import * as signalR from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import {
  ACTION_STATUS,
  HubActionError,
  decodeGameActionResult,
  decodeGetBalanceResult,
  decodeStartGameActionResult,
} from './hubPayloadCodec.js';

const HUB_URL = 'https://slotgamesapi.yogames.win/slots-mp';
const EVENT_TIMEOUT_MS = 15000;
/** Health-check / reconnect probe (StartGameAction). */
const SESSION_PING_TIMEOUT_MS = 10000;
/** Server may push PascalCase or lowercase hub result events. */
const HUB_RESULT_EVENT_NAMES = [
  'GameActionResult',
  'gameactionresult',
  'StartGameActionResult',
  'startgameactionresult',
  'GetBalanceResult',
  'getbalanceresult',
];

/** @returns {string[]} */
function hubEventNames(eventName) {
  const lower = eventName.toLowerCase();
  return lower === eventName ? [eventName] : [eventName, lower];
}

/** @returns {import('@microsoft/signalr').HubConnectionState | null} */
function hubState(connection) {
  return connection?.state ?? null;
}

export class ShiningCrownService {
  constructor() {
    this.connection = null;
    this._connected = false;
    this._connectPromise = null;
    this._connectionHandlers = null;
    this._connectionHandlersAttached = false;
    this._silentHubHandlersAttached = false;
    this._forceDisconnectAttached = false;
    /** While true, intentional stop/restart must not surface as connection-lost UI. */
    this._suppressConnectionEvents = false;
    /** Duplicate-session tab — block new hub invokes/reconnects. */
    this._sessionCeased = false;
    /** @type {string[] | null} Last betOptions from StartGameAction (exact strings for Spin). */
    this._betOptions = null;
    /** Monotonic id so stale GameAction listeners ignore superseded spin invokes. */
    this._spinInvokeSeq = 0;
    /** @type {(() => void) | null} Detaches the in-flight spin hub listener. */
    this._abortSpinInvoke = null;
  }

  /**
   * @param {{
   *   onConnected?: () => void,
   *   onDisconnected?: () => void,
   *   onReconnecting?: () => void,
   *   onForceDisconnect?: (payload?: unknown) => void,
   * } | null} handlers
   */
  setConnectionHandlers(handlers) {
    this._connectionHandlers = handlers;
    this._attachConnectionHandlers();
    this._attachForceDisconnectHandler();
  }

  _attachForceDisconnectHandler() {
    if (!this.connection || this._forceDisconnectAttached) return;
    this._forceDisconnectAttached = true;
    const onForceDisconnect = (payload) => {
      const id =
        payload?.connectionId ??
        payload?.ConnectionId ??
        payload?.connectionID ??
        payload?.ConnectionID;
      // Server includes the new session; ignore when this connection is the successor.
      if (id && this.connection?.connectionId && id === this.connection.connectionId) {
        return;
      }
      // Must stop immediately — automatic reconnect would reopen a handed-over session.
      void this.ceaseSession().finally(() => {
        this._connectionHandlers?.onForceDisconnect?.(payload);
      });
    };
    const duplicateSessionEvents = [
      'ForceDisconnect',
      'forcedisconnect',
      'SessionEnded',
      'sessionended',
      'DuplicateSession',
      'duplicatesession',
      'SessionReplaced',
      'sessionreplaced',
    ];
    for (const name of duplicateSessionEvents) {
      this.connection.on(name, onForceDisconnect);
    }
  }

  /** Hub pushes lowercase event names; register no-op handlers so SignalR does not warn. */
  _attachSilentHubResultHandlers() {
    if (!this.connection || this._silentHubHandlersAttached) return;
    this._silentHubHandlersAttached = true;
    for (const name of HUB_RESULT_EVENT_NAMES) {
      this.connection.on(name, () => {});
    }
  }

  _attachConnectionHandlers() {
    if (!this.connection || this._connectionHandlersAttached) return;
    this._connectionHandlersAttached = true;
    this._attachSilentHubResultHandlers();
    this.connection.onreconnecting(() => {
      this._connected = false;
      if (this._suppressConnectionEvents) return;
      this._connectionHandlers?.onReconnecting?.();
    });
    this.connection.onreconnected(() => {
      this._connected = true;
      this._connectionHandlers?.onConnected?.();
    });
    this.connection.onclose(() => {
      this._connected = false;
      if (this._suppressConnectionEvents) return;
      this._connectionHandlers?.onDisconnected?.();
    });
  }

  async connect(accessToken) {
    if (this._sessionCeased) return;
    if (!this.connection) {
      const urlOptions = accessToken ? { accessTokenFactory: () => accessToken } : {};
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, urlOptions)
        .withHubProtocol(new MessagePackHubProtocol())
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();
      this._attachConnectionHandlers();
      this._attachForceDisconnectHandler();
    }

    const state = this.connection.state;
    if (state === signalR.HubConnectionState.Connected) {
      this._connected = true;
      return;
    }
    if (state === signalR.HubConnectionState.Connecting || state === signalR.HubConnectionState.Reconnecting) {
      if (this._connectPromise) await this._connectPromise;
      return;
    }
    if (this._connectPromise) {
      await this._connectPromise;
      return;
    }

    this._connectPromise = this.connection
      .start()
      .then(() => {
        this._connected = true;
      })
      .finally(() => {
        this._connectPromise = null;
      });
    await this._connectPromise;
  }

  async getInitialState({
    isDemo,
    partnerId,
    gameName,
    timeoutMs,
  } = {}) {
    // MessagePack binding is strict: PartnerId must be a number (string throws on the server).
    const partnerIdNum = Number(partnerId);
    if (!Number.isFinite(partnerIdNum)) {
      throw new Error('PartnerId must be a number');
    }
    const raw = await this._invokeWithEventFallback(
      'StartGameAction',
      'StartGameActionResult',
      {
        GameName: gameName.trim(),
        IsDemo: Boolean(isDemo),
        PartnerId: partnerIdNum,
      },
      timeoutMs ?? EVENT_TIMEOUT_MS,
    );
    const decoded = decodeStartGameActionResult(raw);
    const status = Number(decoded.actionStatus);
    if (status !== ACTION_STATUS.SUCCESS) {
      throw new HubActionError(
        Number.isFinite(status) ? status : ACTION_STATUS.SERVER_FAILURE,
        `StartGameAction failed (${status})`,
      );
    }
    if (Array.isArray(decoded.betOptions) && decoded.betOptions.length) {
      this._betOptions = decoded.betOptions.map(String);
    }
    return decoded;
  }

  /**
   * @param {unknown} amount
   * @returns {string}
   */
  _formatBetAmount(amount) {
    const n = Number(amount);
    if (Array.isArray(this._betOptions)) {
      const match = this._betOptions.find((opt) => Number(opt) === n);
      if (match != null) return String(match);
    }
    return String(amount);
  }

  /** Live session check — same hub call as bootstrap (returns balance + matrix). */
  async pingSession({ isDemo, partnerId, gameName } = {}) {
    return this.getInitialState({
      isDemo,
      partnerId,
      gameName,
      timeoutMs: SESSION_PING_TIMEOUT_MS,
    });
  }

  async getBalance() {
    const raw = await this._invokeWithEventFallback(
      'GetBalance',
      'GetBalanceResult',
      undefined,
      EVENT_TIMEOUT_MS,
    );
    return decodeGetBalanceResult(raw);
  }

  /**
   * @param {unknown} amount Base bet. The hub applies the buy-bonus multiplier.
   * @param {{ buyBonus?: boolean, buyBonusType?: 1 | 2 | 3, guaranteedWins?: boolean }} [options]
   * Live spins omit `options` and send `SpinData: {}`.
   * Buy bonus matches Crown Crush: `ActionType: Spin` plus `SpinData.BuyBonus`.
   */
  async spin(amount, options) {
    const buyBonus = options?.buyBonus === true;
    const invokeSeq = ++this._spinInvokeSeq;
    if (this._abortSpinInvoke) {
      try {
        this._abortSpinInvoke();
      } catch {
        /* ignore */
      }
      this._abortSpinInvoke = null;
    }
    // Prefer the exact betOptions string when it matches numerically (avoids "2" vs "2.00" 400s).
    const amountStr = this._formatBetAmount(amount);
    /** @type {Record<string, unknown>} */
    const spinData = {};
    if (buyBonus) {
      spinData.BuyBonus = true;
      spinData.BuyBonusType = options.buyBonusType;
      if (options.guaranteedWins) spinData.GuaranteedWins = true;
    }
    const raw = await this._invokeWithEventFallback(
      'GameAction',
      'GameActionResult',
      {
        ActionType: 'Spin',
        Model: { Amount: amountStr, SpinData: spinData },
      },
      EVENT_TIMEOUT_MS,
      invokeSeq,
    );
    const decoded = decodeGameActionResult(raw);
    const status = Number(decoded.actionStatus);
    if (status !== ACTION_STATUS.SUCCESS) {
      throw new HubActionError(
        Number.isFinite(status) ? status : ACTION_STATUS.SERVER_FAILURE,
        `GameAction failed (${status})`,
      );
    }
    // Doc: actionType is null on some refusals; ignore non-Spin success frames.
    if (decoded.actionType && decoded.actionType !== 'Spin') {
      throw new HubActionError(
        ACTION_STATUS.REJECTED,
        `Unexpected actionType: ${decoded.actionType}`,
      );
    }
    return decoded;
  }

  async disconnect() {
    if (!this.connection) return;
    this._suppressConnectionEvents = true;
    try {
      await this.connection.stop();
    } catch (_) {
      /* ignore */
    } finally {
      this._suppressConnectionEvents = false;
    }
    this._connected = false;
  }

  /** Stop hub and automatic reconnect (kicked / duplicate session tab). */
  async ceaseSession() {
    this._sessionCeased = true;
    return this.disconnect();
  }

  /** Stop and start the hub (iOS resume / stale WebSocket). */
  async restartConnection() {
    if (this._sessionCeased) return;
    if (!this.connection) return;
    const state = hubState(this.connection);
    if (
      state === signalR.HubConnectionState.Connecting ||
      state === signalR.HubConnectionState.Reconnecting
    ) {
      if (this._connectPromise) await this._connectPromise;
      return;
    }
    this._suppressConnectionEvents = true;
    try {
      if (state === signalR.HubConnectionState.Connected) {
        try {
          await this.connection.stop();
        } catch (_) {
          /* ignore */
        }
      }
      this._connected = false;
      await this.connection.start();
      this._connected = true;
    } finally {
      this._suppressConnectionEvents = false;
    }
  }

  /** Live hub socket is up (not merely mid-reconnect). */
  isConnected() {
    const connected =
      hubState(this.connection) === signalR.HubConnectionState.Connected;
    if (connected) this._connected = true;
    return connected;
  }

  /** SignalR automatic reconnect in progress. */
  isReconnecting() {
    const s = hubState(this.connection);
    return (
      s === signalR.HubConnectionState.Connecting ||
      s === signalR.HubConnectionState.Reconnecting
    );
  }

  async _invokeWithEventFallback(method, eventName, payload, timeoutMs = EVENT_TIMEOUT_MS, invokeSeq = null) {
    if (this._sessionCeased) {
      return Promise.reject(new Error('Session ceased'));
    }
    await this.connect();
    if (this._sessionCeased) {
      return Promise.reject(new Error('Session ceased'));
    }
    return new Promise((resolve, reject) => {
      let timer = null;
      let settled = false;
      const finish = (cb, value) => {
        if (settled) return;
        if (invokeSeq != null && invokeSeq !== this._spinInvokeSeq) {
          settled = true;
          if (timer) clearTimeout(timer);
          for (const name of eventNames) {
            this.connection.off(name, listener);
          }
          if (this._abortSpinInvoke === abort) this._abortSpinInvoke = null;
          return;
        }
        settled = true;
        if (timer) clearTimeout(timer);
        for (const name of eventNames) {
          this.connection.off(name, listener);
        }
        if (this._abortSpinInvoke === abort) this._abortSpinInvoke = null;
        if (
          cb === resolve &&
          hubState(this.connection) === signalR.HubConnectionState.Connected
        ) {
          this._connected = true;
        }
        cb(value);
      };
      const listener = (value) => finish(resolve, value);
      const eventNames = hubEventNames(eventName);
      const abort = () => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        for (const name of eventNames) {
          this.connection.off(name, listener);
        }
        if (this._abortSpinInvoke === abort) this._abortSpinInvoke = null;
        reject(new Error('Spin superseded'));
      };
      if (method === 'GameAction' && invokeSeq != null) {
        this._abortSpinInvoke = abort;
      }
      for (const name of eventNames) {
        this.connection.on(name, listener);
      }
      const invokePromise =
        payload === undefined
          ? this.connection.invoke(method)
          : this.connection.invoke(method, payload);
      invokePromise
        .then((direct) => {
          if (direct != null) finish(resolve, direct);
        })
        .catch((err) => finish(reject, err));
      timer = setTimeout(
        () => finish(reject, new Error(`${eventName} timeout`)),
        timeoutMs,
      );
    });
  }
}
